<template>
  <div style="margin:20px">

    <FiPageRail title="Actuals Stats">
      <template #actions>
          <b-button size="sm" variant="primary" :to="back_link">
            <i class="fa fa-arrow-left" style="margin-right: 4px;"></i> <b>Back to Actuals</b>
          </b-button>
          <actuals-stats-help :source="source" style="margin-left: 8px;"/>
      </template>
    </FiPageRail>

    <div style="font-size: 22px; font-weight: bold; position: relative; margin: 20px" class="d-flex align-items-center justify-content-center">
      <b-button size="sm" variant="outline-secondary" title="Previous day" v-on:click.prevent="shift_dates(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>
      &nbsp;to&nbsp;
      <date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"></date-picker>&nbsp;
      <b-button size="sm" variant="outline-secondary" title="Next day" v-on:click.prevent="shift_dates(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      <span v-if="base_selector_visible">Base&nbsp;<b-form-select v-model="selected_base" :options="base_options" style="font-size: 16px; font-weight: bold; width:220px"/>&nbsp;&nbsp;</span>
      <b-button v-on:click.prevent="load_data()" :disabled="loading || base_selection_required">Get Results</b-button>
    </div>

    <div v-if="loading">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="base_selector_visible && !summary.totals" class="pva-empty">
      <span v-if="base_selection_required">Select a base, then click <strong>Get Results</strong> to load stats.</span>
      <span v-else>Click <strong>Get Results</strong> to load stats for this base.</span>
    </div>

    <div v-else-if="summary.totals">
      <div class="pva-section pva-glance-section">
        <div class="pva-glance-header">
          <div class="pva-section-header pva-glance-header-title">At a Glance</div>
          <div class="pva-summary-stats">
            <span class="pva-summary-stat"><strong>{{ summary.totals.movements }}</strong> {{ count_label }}</span>
            <span class="pva-summary-stat pva-summary-stat-success"><strong>{{ summary.totals.success }}</strong> successful</span>
            <span class="pva-summary-stat pva-summary-stat-error"><strong>{{ summary.totals.error || 0 }}</strong> errors</span>
            <span v-if="summary.totals.outliers" class="pva-summary-stat pva-summary-stat-warn"><strong>{{ summary.totals.outliers }}</strong> outliers (&ge; {{ summary.outlier_threshold_minutes }}m)</span>
          </div>
        </div>

        <div v-if="summary.totals.with_hos_times === 0" class="pva-empty">
          No successful actuals with both HOS start and finish times found between {{ summary.start_date }} and {{ summary.end_date }}.
        </div>

        <template v-else>
          <div class="pva-glance-grid">
            <div
              v-for="tile in milestone_glance_tiles"
              :key="'glance-' + tile.key"
              class="pva-glance-tile"
              :class="card_theme_class(tile.key)"
            >
              <div class="pva-glance-label">{{ tile.label }}</div>
              <div class="pva-glance-stats">
                <div class="pva-glance-stat">
                  <span class="pva-glance-value">{{ format_minutes(tile.median_abs_delta) }}</span>
                  <span class="pva-glance-caption">median</span>
                </div>
                <div class="pva-glance-stat">
                  <span class="pva-glance-value">{{ tile.on_time_pct }}%</span>
                  <span class="pva-glance-caption">on time (&plusmn;{{ on_time_threshold }}m)</span>
                </div>
              </div>
              <div class="pva-glance-meta">
                <span>{{ tile.count }} with times</span>
                <span v-if="tile.mean_delta != null" :class="delta_class(tile.mean_delta)">
                  avg {{ format_signed_minutes(tile.mean_delta) }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="glance_highlights.length" class="pva-glance-highlights">
            <span
              v-for="(highlight, idx) in glance_highlights"
              :key="'highlight-' + idx"
              class="pva-glance-highlight"
            >{{ highlight }}</span>
          </div>
        </template>
      </div>

      <div v-if="on_time_metric_tiles.length" class="pva-section">
        <div class="pva-section-header">
          Driver On-Time %
          <router-link
            :to="on_time_report_link"
            target="_blank"
            style="float: right; font-size: 13px;"
          >
            View Full Report <i class="fa fa-external-link" aria-hidden="true"></i>
          </router-link>
        </div>
        <div class="pva-side-note">Pass/fail gates for punctuality at key milestones, separate from the &plusmn;30m accuracy window used above.</div>
        <div class="pva-ontime-grid">
          <div
            v-for="tile in on_time_metric_tiles"
            :key="'ontime-' + tile.key"
            class="pva-ontime-tile"
            :class="card_theme_class(tile.key)"
          >
            <div class="pva-ontime-label">{{ tile.label }}</div>
            <div class="pva-ontime-value" :class="on_time_pct_class(tile.on_time_pct)">{{ tile.on_time_pct }}%</div>
            <div class="pva-ontime-meta">{{ tile.on_time_count }} of {{ tile.count }}</div>
            <div class="pva-ontime-rule">{{ tile.rule }}</div>
          </div>
        </div>
      </div>

      <template v-if="summary.totals.with_hos_times > 0">
        <div class="pva-section">
          <div class="pva-section-header">Milestone Accuracy Details</div>
        <div class="pva-accuracy-grid">
          <div
            v-for="(row, rowIdx) in accuracy_card_rows"
            :key="'accuracy-row-' + rowIdx"
            class="pva-accuracy-row"
          >
            <div
              v-for="card in row"
              :key="card.key"
              class="pva-accuracy-card"
              :class="card_theme_class(card.key)"
            >
              <div class="pva-accuracy-card-header">{{ card.label }} Accuracy</div>
              <div v-if="milestone_accuracy_for(card.key).count === 0" class="pva-accuracy-empty">
                No successful actuals with both planned and actual times.
              </div>
              <template v-else>
                <div class="pva-simple-headline">
                  <div class="pva-simple-value" :class="on_time_pct_class(on_time_pct_for(card.key))">{{ on_time_pct_for(card.key) }}%</div>
                  <div class="pva-simple-caption">on time (within &plusmn;{{ on_time_threshold }}m) &middot; {{ milestone_accuracy_for(card.key).count }} {{ count_label }} measured</div>
                </div>
                <div class="pva-simple-summary">
                  <div>
                    {{ typical_gap_text(card.key) }}<template v-if="tendency_text(card.key)"> &mdash; {{ tendency_text(card.key) }}</template>
                  </div>
                  <div v-if="attention_count(card.key) > 0" class="pva-simple-flag">
                    {{ attention_count(card.key) }} needed a closer look (more than {{ on_time_threshold }}m off)
                  </div>
                </div>
                <table class="pva-table pva-table-narrow">
                  <tr><th>Within</th><th>%</th><th></th></tr>
                  <tr
                    v-for="entry in simplified_within_minutes(card.key)"
                    :key="card.key + '-within-' + entry.minutes"
                  >
                    <td>{{ entry.minutes }}m</td>
                    <td>{{ entry.pct }}%</td>
                    <td class="pva-bar-cell">
                      <div class="pva-bar" :class="bar_class(card.key)" :style="{ width: entry.pct + '%' }"></div>
                    </td>
                  </tr>
                </table>
              </template>
            </div>
          </div>
        </div>
        </div>

        <div v-if="outliers.length > 0" class="pva-section">
          <div class="pva-section-header">
            Likely Invalid Outliers
            <span class="pva-section-count">{{ summary.totals.outliers }} {{ count_label }} with HOS &ge; {{ summary.outlier_threshold_minutes }}m from planned</span>
          </div>
          <div class="pva-side-note">
            These successful actuals have HOS start or finish times far from planned &mdash; often a sign of wrong driver login, overlapping bookings, or bad telematics matching.
          </div>
          <table class="pva-table">
            <tr>
              <th>Booking</th>
              <th>Vehicle</th>
              <th>Driver</th>
              <th>Planned Start</th>
              <th>HOS Start</th>
              <th>Start &Delta;</th>
              <th>Planned Finish</th>
              <th>HOS Finish</th>
              <th>Finish &Delta;</th>
              <th>Max &Delta;</th>
            </tr>
            <tr v-for="row in visible_outliers" :key="row.booking_hash + row.max_abs_delta">
              <td><router-link :to="booking_link(row)">{{ booking_label(row) }}</router-link></td>
              <td>{{ row.vehicle }}</td>
              <td>{{ row.driver_name || '' }}</td>
              <td>{{ row.planned_start | formatDateTimeSmall }}</td>
              <td>{{ row.hos_start | formatDateTimeSmall }}</td>
              <td :class="delta_class(row.start_delta_minutes)">{{ format_signed_minutes(row.start_delta_minutes) }}</td>
              <td>{{ row.planned_finish | formatDateTimeSmall }}</td>
              <td>{{ row.hos_finish | formatDateTimeSmall }}</td>
              <td :class="delta_class(row.finish_delta_minutes)">{{ format_signed_minutes(row.finish_delta_minutes) }}</td>
              <td class="pva-outlier-max">{{ row.max_abs_delta }}m</td>
            </tr>
          </table>
          <div v-if="outliers.length > outlier_preview_count || summary.totals.outliers > outliers.length" class="pva-outlier-actions">
            <b-button
              v-if="!outliers_expanded && outliers.length > outlier_preview_count"
              size="sm"
              variant="outline-secondary"
              v-on:click.prevent="outliers_expanded = true"
            >
              Show all {{ outlier_expand_count }} outliers
            </b-button>
            <b-button
              v-if="outliers_expanded"
              size="sm"
              variant="outline-secondary"
              v-on:click.prevent="outliers_expanded = false"
            >
              Show top {{ outlier_preview_count }}
            </b-button>
            <span v-if="summary.totals.outliers > outliers.length" class="pva-more-note">
              showing {{ visible_outliers.length }} of {{ summary.totals.outliers }} outliers
            </span>
          </div>
        </div>

        <div class="pva-bottom-row">
          <div v-if="by_day.length > 0" class="pva-bottom-col">
            <div class="pva-section-header">Trend by Day</div>
            <table class="pva-table pva-table-narrow">
              <tr><th>Date</th><th>Count</th><th>Start Median</th><th>Finish Median</th><th>Outliers</th></tr>
              <tr v-for="day in by_day" :key="day.date">
                <td>{{ day.date }}</td>
                <td>{{ day.count }}</td>
                <td>{{ format_minutes(day.start_median_abs) }}</td>
                <td>{{ format_minutes(day.finish_median_abs) }}</td>
                <td>{{ day.outliers || 0 }}</td>
              </tr>
            </table>
          </div>

          <div v-if="by_booking_type.length > 0" class="pva-bottom-col">
            <div class="pva-section-header">By Booking Type</div>
            <table class="pva-table pva-table-narrow">
              <tr><th>Type</th><th>With HOS</th><th>Start Median</th><th>Finish Median</th><th>Within 30m Start</th><th>Within 30m Finish</th></tr>
              <tr v-for="entry in by_booking_type" :key="entry.booking_type">
                <td>{{ entry.booking_type }}</td>
                <td>{{ entry.with_hos }}</td>
                <td>{{ format_minutes(entry.start_median_abs) }}</td>
                <td>{{ format_minutes(entry.finish_median_abs) }}</td>
                <td>{{ entry.start_within_30m_pct }}%</td>
                <td>{{ entry.finish_within_30m_pct }}%</td>
              </tr>
            </table>
          </div>
        </div>

        <div v-if="by_vehicle_type.length > 0" class="pva-section">
          <div class="pva-section-header">By Vehicle Type</div>
          <table class="pva-table pva-table-narrow">
            <tr><th>Type</th><th>With HOS</th><th>Start Median</th><th>Finish Median</th><th>Within 30m Start</th><th>Within 30m Finish</th></tr>
            <tr v-for="entry in by_vehicle_type" :key="entry.vehicle_type">
              <td>{{ entry.vehicle_type }}</td>
              <td>{{ entry.with_hos }}</td>
              <td>{{ format_minutes(entry.start_median_abs) }}</td>
              <td>{{ format_minutes(entry.finish_median_abs) }}</td>
              <td>{{ entry.start_within_30m_pct }}%</td>
              <td>{{ entry.finish_within_30m_pct }}%</td>
            </tr>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import {get_actuals_planned_vs_actual_summary} from "../networking/apis"
import {get_tbn_actuals_planned_vs_actual_summary, get_booking_bases} from "../networking/tbn_apis"
import actuals_stats_help from '@/components/actuals_stats_help'

export default {
  name: 'actuals_stats',

  components: {
    'actuals-stats-help': actuals_stats_help,
  },

  data() {
    return {
      loading: false,
      summary: {},
      start_accuracy: {},
      finish_accuracy: {},
      milestone_accuracy: {},
      on_time_metrics: {},
      by_day: [],
      by_booking_type: [],
      by_vehicle_type: [],
      outliers: [],
      outliers_expanded: false,
      outlier_preview_count: 5,
      outlier_max_count: 25,
      initial_load_done: false,
      details_fromDate: justDate(moment().subtract(6, 'days')),
      details_toDate: justDate(moment()),
      actuals_filter: 'finish',
      selected_company: '',
      selected_type: '',
      selected_base: '',
      base_options: [],
      base_required: false,
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(180, 'days'),
        maxDate: moment().add(1, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    source() {
      const source = this.$route.query.source
      return ['cm', 'tbn', 'fasttrak'].includes(source) ? source : 'cm'
    },
    // base filtering is TBN-only; load_bases leaves base_options empty for the
    // other sources, so the selector and its empty state never appear there.
    base_selector_visible() {
      return this.base_options.length > 2
    },
    // TBN's actuals query returns nothing for tenants with many yards unless a
    // base is supplied (same gate the TBN actuals page enforces), so the stats
    // are held back until one is picked rather than rendering a silent zero.
    base_selection_required() {
      return this.base_required && !this.selected_base
    },
    count_label() {
      return this.source === 'tbn' ? 'bookings' : 'movements'
    },
    back_link() {
      const paths = { cm: '/actuals', tbn: '/actuals_tbn', fasttrak: '/actuals_fasttrak' }
      const query = {
        fromDate: this.details_fromDate,
        toDate: this.details_toDate,
        filter: this.actuals_filter,
      }
      if (this.source === 'tbn')
        query.base = this.selected_base
      else {
        query.company = this.selected_company
        query.type = this.selected_type
      }
      return { path: paths[this.source], query }
    },
    on_time_threshold() {
      return this.summary.on_time_threshold_minutes || 30
    },
    visible_outliers() {
      const limit = this.outliers_expanded ? this.outlier_max_count : this.outlier_preview_count
      return (this.outliers || []).slice(0, limit)
    },
    outlier_expand_count() {
      return Math.min((this.outliers || []).length, this.outlier_max_count)
    },
    accuracy_card_rows() {
      if (this.source === 'tbn') {
        return [
          [
            { key: 'hos_start', label: 'HOS Start' },
            { key: 'hos_finish', label: 'HOS Finish' },
          ],
          [
            { key: 'vehicle_start', label: 'First Departure' },
            { key: 'vehicle_finish', label: 'Last Arrival' },
          ],
        ]
      }
      return [
        [
          { key: 'hos_start', label: 'HOS Start' },
          { key: 'hos_finish', label: 'HOS Finish' },
        ],
        [
          { key: 'spot', label: 'Spot' },
          { key: 'pickup', label: 'Pickup' },
        ],
        [
          { key: 'arrival', label: 'Arrival' },
          { key: 'leave', label: 'Leave' },
        ],
        [
          { key: 'back', label: 'Back' },
          { key: 'vehicle_finish', label: 'Finish' },
        ],
      ]
    },
    milestone_glance_tiles() {
      return this.accuracy_card_rows.flat().map(card => {
        const accuracy = this.milestone_accuracy_for(card.key)
        return {
          key: card.key,
          label: card.label,
          count: accuracy.count || 0,
          median_abs_delta: accuracy.median_abs_delta,
          mean_delta: accuracy.mean_delta,
          on_time_pct: this.pct_of(accuracy.on_time_count, accuracy.count),
        }
      })
    },
    on_time_metric_tiles() {
      const defs = [
        {
          key: 'to_yard',
          label: 'On Time To Yard',
          rule: this.source === 'tbn'
            ? 'HOS on duty & assigned to vehicle at or before the yard time (scheduled driver report time)'
            : 'HOS on duty & assigned to vehicle at or before planned start',
        },
        {
          key: 'depart_yard',
          label: 'On Time To Depart Yard',
          rule: this.source === 'tbn'
            ? 'Vehicle departure (first departure) at or before the planned depart time'
            : 'Vehicle departure within 30m after planned start',
        },
        {
          key: 'to_pickup',
          label: 'On Time To Pickup',
          rule: this.source === 'tbn'
            ? 'Arrived at the first pickup stop at or before its planned arrival time'
            : 'Arrived at pickup location (position time) at least 15m before planned pickup/departure time',
        },
      ]
      return defs
        .map(def => ({ ...def, ...(this.on_time_metrics[def.key] || {}) }))
        .filter(tile => tile.count > 0)
    },
    on_time_report_link() {
      return {
        name: this.source === 'tbn' ? 'actuals_on_time_report_tbn' : 'actuals_on_time_report',
        query: {
          fromDate: this.details_fromDate,
          toDate: this.details_toDate,
        },
      }
    },
    glance_highlights() {
      const tiles = this.milestone_glance_tiles.filter(tile => tile.count > 0)
      if (!tiles.length)
        return []

      const highlights = []
      const onTimeTiles = tiles.filter(tile => parseFloat(tile.on_time_pct) > 0)
      const bestOnTime = onTimeTiles.reduce((best, tile) => (
        !best || parseFloat(tile.on_time_pct) > parseFloat(best.on_time_pct) ? tile : best
      ), null)
      const worstMedian = tiles.reduce((worst, tile) => (
        !worst || (tile.median_abs_delta != null && tile.median_abs_delta > (worst.median_abs_delta || 0))
          ? tile : worst
      ), null)
      const mostLate = tiles.reduce((worst, tile) => {
        const accuracy = this.milestone_accuracy_for(tile.key)
        return !worst || (accuracy.late_count || 0) > (this.milestone_accuracy_for(worst.key).late_count || 0)
          ? tile : worst
      }, null)

      if (bestOnTime)
        highlights.push(`Best on-time: ${bestOnTime.label} (${bestOnTime.on_time_pct}% within ±${this.on_time_threshold}m)`)
      if (worstMedian && worstMedian.median_abs_delta != null)
        highlights.push(`Largest median gap: ${worstMedian.label} (${this.format_minutes(worstMedian.median_abs_delta)})`)
      if (mostLate) {
        const lateCount = this.milestone_accuracy_for(mostLate.key).late_count || 0
        if (lateCount > 0)
          highlights.push(`Most late events: ${mostLate.label} (${lateCount} late)`)
      }
      if (this.summary.totals && this.summary.totals.outliers)
        highlights.push(`${this.summary.totals.outliers} HOS outlier ${this.count_label} (≥${this.summary.outlier_threshold_minutes}m from planned)`)

      return highlights
    },
  },

  async created() {
    if (this.$route.query.fromDate)
      this.details_fromDate = this.$route.query.fromDate
    if (this.$route.query.toDate)
      this.details_toDate = this.$route.query.toDate
    if (this.$route.query.filter)
      this.actuals_filter = this.$route.query.filter
    if (this.$route.query.company)
      this.selected_company = this.$route.query.company
    if (this.$route.query.type)
      this.selected_type = this.$route.query.type
    if (this.$route.query.base)
      this.selected_base = this.$route.query.base
    await this.load_bases()
    await this.try_initial_load()
  },

  watch: {
    user: {
      deep: true,
      async handler() {
        await this.load_bases()
        this.try_initial_load()
      },
    },
    selected_base() {
      this.summary = {}
    },
  },

  methods: {
    async load_bases() {
      if (this.source !== 'tbn' || !this.user || !this.user.tenant)
        return
      try {
        const { value: { data = {} } } = await get_booking_bases(this.user.tenant)
        const options = (data.summary && data.summary.base_options) || []
        const yardCount = Math.max(0, options.length - 1)
        this.base_required = yardCount > 15
        if (this.base_required && options.length > 0) {
          options[0] = { ...options[0], value: '', text: 'Select Base' }
        }
        this.base_options = options
      } catch (e) {
        this.$emit('showAlert', 'error retrieving bases, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    async try_initial_load() {
      if (this.initial_load_done || !this.user || !this.user.tenant || this.base_selection_required)
        return
      this.initial_load_done = true
      await this.load_data()
    },
    async load_data() {
      if (!this.user || !this.user.tenant)
        return
      if (this.base_selection_required) {
        this.$emit('showAlert', 'please select a base before loading stats', 'warning', 5000)
        return
      }
      this.loading = true
      try {
        const from = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const to = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')

        const api = this.source === 'tbn'
          ? get_tbn_actuals_planned_vs_actual_summary
          : get_actuals_planned_vs_actual_summary
        const { value: { data = {} }} = this.source === 'tbn'
          ? await api(this.user.tenant, from, to, this.actuals_filter, this.selected_base)
          : await api(this.user.tenant, from, to, this.actuals_filter, this.selected_company, this.selected_type)

        this.summary = data.summary || {}
        this.start_accuracy = data.start_accuracy || {}
        this.finish_accuracy = data.finish_accuracy || {}
        this.milestone_accuracy = data.milestone_accuracy || {}
        this.on_time_metrics = data.on_time_metrics || {}
        this.by_day = data.by_day || []
        this.by_booking_type = data.by_booking_type || []
        this.by_vehicle_type = data.by_vehicle_type || []
        this.outliers = data.outliers || []
        this.outliers_expanded = false
        if (this.summary.timezone)
          moment.tz.setDefault(this.summary.timezone)
      } catch (e) {
        this.$emit('showAlert', 'error loading data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    shift_dates(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
    },
    booking_label(booking) {
      return booking.booking_id || booking.booking_hash
    },
    booking_link(booking) {
      const paths = { cm: '/actuals', tbn: '/actuals_tbn', fasttrak: '/actuals_fasttrak' }
      return { path: paths[this.source], query: { booking_id: this.booking_label(booking) } }
    },
    format_minutes(value) {
      if (value == null)
        return '—'
      return `${Math.round(value)}m`
    },
    format_signed_minutes(value) {
      if (value == null)
        return '—'
      const rounded = Math.round(value)
      return `${rounded > 0 ? '+' : ''}${rounded}m`
    },
    on_time_pct_class(pct) {
      if (pct == null)
        return ''
      if (pct >= 90)
        return 'pva-on-time-good'
      if (pct < 70)
        return 'pva-on-time-bad'
      return ''
    },
    pct_of(count, total) {
      if (!total)
        return '0'
      return Math.round(100.0 * count / total)
    },
    delta_class(minutes) {
      if (minutes == null)
        return ''
      const abs = Math.abs(minutes)
      if (abs >= 120)
        return 'pva-delta-bad'
      if (abs >= 30)
        return 'pva-delta-warn'
      return ''
    },
    milestone_accuracy_for(key) {
      return this.milestone_accuracy[key] || {}
    },
    on_time_pct_for(key) {
      const acc = this.milestone_accuracy_for(key)
      return this.pct_of(acc.on_time_count, acc.count)
    },
    typical_gap_text(key) {
      const acc = this.milestone_accuracy_for(key)
      if (acc.median_abs_delta == null)
        return ''
      const minutes = Math.round(acc.median_abs_delta)
      if (minutes === 0)
        return 'Usually right on schedule'
      return `Usually within ${minutes}m of schedule`
    },
    tendency_text(key) {
      const acc = this.milestone_accuracy_for(key)
      const early = acc.early_count || 0
      const late = acc.late_count || 0
      if (late > 0 && late > early * 2)
        return 'tends to run late'
      if (early > 0 && early > late * 2)
        return 'tends to run early'
      return ''
    },
    attention_count(key) {
      const acc = this.milestone_accuracy_for(key)
      return (acc.early_count || 0) + (acc.late_count || 0)
    },
    simplified_within_minutes(key) {
      const keepMinutes = [15, 30, 60]
      return (this.milestone_accuracy_for(key).within_minutes || [])
        .filter(entry => keepMinutes.includes(entry.minutes))
    },
    card_theme_class(key) {
      const themes = {
        hos_start: 'pva-card-theme-hos-start',
        hos_finish: 'pva-card-theme-hos-finish',
        spot: 'pva-card-theme-spot',
        pickup: 'pva-card-theme-pickup',
        arrival: 'pva-card-theme-arrival',
        leave: 'pva-card-theme-leave',
        back: 'pva-card-theme-back',
        vehicle_start: 'pva-card-theme-vehicle-start',
        vehicle_finish: 'pva-card-theme-finish',
        to_yard: 'pva-card-theme-hos-start',
        depart_yard: 'pva-card-theme-vehicle-start',
        to_pickup: 'pva-card-theme-pickup',
      }
      return themes[key] || 'pva-card-theme-default'
    },
    bar_class(key) {
      const barThemes = {
        hos_start: 'pva-bar-hos-start',
        hos_finish: 'pva-bar-hos-finish',
        spot: 'pva-bar-spot',
        pickup: 'pva-bar-pickup',
        arrival: 'pva-bar-arrival',
        leave: 'pva-bar-leave',
        back: 'pva-bar-back',
        vehicle_start: 'pva-bar-vehicle-start',
        vehicle_finish: 'pva-bar-finish',
      }
      return barThemes[key] || 'pva-bar-start'
    },
  },
}
</script>

<style scoped>
.pva-glance-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 2px solid #888;
}
.pva-section-header.pva-glance-header-title {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}
.pva-summary-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: baseline;
  gap: 6px 18px;
  font-size: 15px;
  font-weight: normal;
  color: #666;
  text-align: right;
}
.pva-summary-stat strong {
  font-weight: 600;
  font-size: 16px;
  color: #444;
}
.pva-summary-stat-success strong { color: #155724; }
.pva-summary-stat-error strong { color: #721c24; }
.pva-summary-stat-warn strong { color: #856404; }
.pva-empty {
  font-size: 20px;
  margin: 40px;
  text-align: center;
}
.pva-section {
  margin-bottom: 30px;
  text-align: left;
}
.pva-section-header {
  font-size: 20px;
  font-weight: bold;
  border-bottom: 2px solid #888;
  margin-bottom: 10px;
  padding-bottom: 4px;
  text-align: left;
}
.pva-section-count {
  font-size: 15px;
  font-weight: normal;
  font-style: italic;
  margin-left: 12px;
  color: #555;
}
.pva-simple-headline {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.pva-simple-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
}
.pva-simple-caption {
  font-size: 13px;
  color: #555;
}
.pva-simple-summary {
  font-size: 14px;
  margin-bottom: 10px;
}
.pva-simple-flag {
  color: #856404;
  font-weight: 600;
  margin-top: 2px;
}
.pva-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: 12px;
}
.pva-table th, .pva-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  text-align: left;
}
.pva-table th { background-color: #f5f5f5; }
.pva-table-narrow { max-width: 700px; }
.pva-bar-cell { width: 35%; }
.pva-bar {
  height: 12px;
  border-radius: 2px;
}
.pva-bar-start { background-color: #17a2b8; }
.pva-bar-finish { background-color: #007bff; }
.pva-bar-hos-start { background-color: #17a2b8; }
.pva-bar-hos-finish { background-color: #0d6efd; }
.pva-bar-spot { background-color: #6f42c1; }
.pva-bar-pickup { background-color: #198754; }
.pva-bar-arrival { background-color: #fd7e14; }
.pva-bar-leave { background-color: #e67e22; }
.pva-bar-back { background-color: #d63384; }
.pva-bar-vehicle-start { background-color: #28a745; }
.pva-side-note {
  font-style: italic;
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
}
.pva-more-note {
  font-style: italic;
  font-size: 13px;
  color: #555;
  margin-top: 4px;
}
.pva-outlier-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}
.pva-outlier-actions .pva-more-note {
  margin-top: 0;
}
.pva-bottom-row {
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
  margin-bottom: 30px;
}
.pva-bottom-col {
  flex: 1;
  min-width: 300px;
  text-align: left;
}
.pva-delta-warn { color: #856404; font-weight: bold; }
.pva-delta-bad { color: #721c24; font-weight: bold; }
.pva-outlier-max { font-weight: bold; color: #721c24; }
.pva-on-time-good { color: #155724; font-weight: bold; }
.pva-on-time-bad { color: #721c24; font-weight: bold; }
.pva-ontime-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.pva-ontime-tile {
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid transparent;
  border-left-width: 4px;
  min-width: 0;
}
.pva-ontime-label {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 6px;
}
.pva-ontime-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
}
.pva-ontime-meta {
  font-size: 12px;
  color: #555;
  margin-top: 4px;
}
.pva-ontime-rule {
  font-size: 12px;
  font-style: italic;
  color: #666;
  margin-top: 6px;
}
@media (max-width: 900px) {
  .pva-ontime-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}
.pva-glance-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}
.pva-glance-tile {
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid transparent;
  border-left-width: 4px;
  min-width: 0;
}
.pva-glance-label {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}
.pva-glance-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
}
.pva-glance-stat {
  display: flex;
  flex-direction: column;
}
.pva-glance-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
}
.pva-glance-caption {
  font-size: 12px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.pva-glance-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #555;
  flex-wrap: wrap;
}
.pva-glance-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pva-glance-highlight {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 6px;
  background: #eef2f7;
  border: 1px solid #d5dde8;
  font-size: 13px;
  color: #334;
}
.pva-accuracy-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pva-accuracy-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}
.pva-accuracy-card {
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 0;
  border: 1px solid transparent;
  border-left-width: 5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.pva-accuracy-card-header {
  font-size: 18px;
  font-weight: bold;
  border-bottom: 2px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 10px;
  padding-bottom: 4px;
  text-align: left;
}
.pva-card-theme-hos-start {
  background: #e7f6fa;
  border-color: #b8e4ee;
  border-left-color: #17a2b8;
}
.pva-card-theme-hos-start .pva-accuracy-card-header { color: #0b6674; }
.pva-card-theme-hos-finish {
  background: #e8f0fe;
  border-color: #c5d7fb;
  border-left-color: #0d6efd;
}
.pva-card-theme-hos-finish .pva-accuracy-card-header { color: #0a4fad; }
.pva-card-theme-spot {
  background: #f1ebfa;
  border-color: #d5c4f0;
  border-left-color: #6f42c1;
}
.pva-card-theme-spot .pva-accuracy-card-header { color: #4e2d91; }
.pva-card-theme-pickup {
  background: #e8f6ee;
  border-color: #bfe6ce;
  border-left-color: #198754;
}
.pva-card-theme-pickup .pva-accuracy-card-header { color: #146c43; }
.pva-card-theme-arrival {
  background: #fff3e6;
  border-color: #ffd8a8;
  border-left-color: #fd7e14;
}
.pva-card-theme-arrival .pva-accuracy-card-header { color: #b35c00; }
.pva-card-theme-leave {
  background: #fff8e6;
  border-color: #ffe08a;
  border-left-color: #f0ad00;
}
.pva-card-theme-leave .pva-accuracy-card-header { color: #9a7200; }
.pva-card-theme-back {
  background: #fceaf3;
  border-color: #f0c4dc;
  border-left-color: #d63384;
}
.pva-card-theme-back .pva-accuracy-card-header { color: #9c1f5f; }
.pva-card-theme-vehicle-start {
  background: #eef8f0;
  border-color: #c3e6cb;
  border-left-color: #28a745;
}
.pva-card-theme-vehicle-start .pva-accuracy-card-header { color: #1e7e34; }
.pva-card-theme-finish {
  background: #ecebff;
  border-color: #cbc9fa;
  border-left-color: #4c3fef;
}
.pva-card-theme-finish .pva-accuracy-card-header { color: #3429b8; }
.pva-card-theme-default {
  background: #f8f9fa;
  border-color: #dee2e6;
  border-left-color: #6c757d;
}
.pva-accuracy-empty {
  font-style: italic;
  color: #666;
  font-size: 14px;
}
@media (max-width: 1100px) {
  .pva-glance-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
